/**
 * Google Maps Script Loader
 * Loads Google Maps API script with Places library dynamically
 */

import { logError, logInfo, logWarn } from "./logger.js";

let scriptLoaded = false;
let loadPromise = null;

/**
 * Load Google Maps API script with Places library
 * @returns {Promise<Object>} Google Maps API object
 */
export function loadGoogleMapsScript() {
  // Return cached promise if already loading or loaded
  if (loadPromise) {
    return loadPromise;
  }

  // If already loaded, return immediately
  if (scriptLoaded && window.google && window.google.maps) {
    return Promise.resolve(window.google.maps);
  }

  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

  if (!apiKey) {
    logWarn("Google Maps API key not found in environment variables");
    return Promise.reject(
      new Error("Google Maps API key not configured (VITE_GOOGLE_MAPS_API_KEY)")
    );
  }

  loadPromise = new Promise((resolve, reject) => {
    // Check if script is already in the DOM
    const existingScript = document.querySelector(
      'script[src*="maps.googleapis.com"]'
    );

    if (existingScript) {
      // Script exists, wait for it to load
      if (window.google && window.google.maps) {
        scriptLoaded = true;
        logInfo("Google Maps script already loaded");
        resolve(window.google.maps);
        return;
      }

      // Script exists but not loaded yet, wait for load event
      existingScript.addEventListener("load", () => {
        scriptLoaded = true;
        logInfo("Google Maps script loaded from existing script");
        resolve(window.google.maps);
      });

      existingScript.addEventListener("error", () => {
        logError("Failed to load Google Maps script");
        reject(new Error("Failed to load Google Maps script"));
      });

      return;
    }

    // Create new script element
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.async = true;
    script.defer = true;

    script.onload = () => {
      scriptLoaded = true;
      logInfo("Google Maps script loaded successfully");
      resolve(window.google.maps);
    };

    script.onerror = () => {
      logError("Failed to load Google Maps script");
      loadPromise = null; // Reset promise so we can retry
      reject(new Error("Failed to load Google Maps script"));
    };

    document.head.appendChild(script);
  });

  return loadPromise;
}

/**
 * Check if Google Maps is loaded
 * @returns {boolean} True if Google Maps is loaded
 */
export function isGoogleMapsLoaded() {
  return scriptLoaded && window.google && window.google.maps;
}

